/**
 * Theme resolution for `@richrich/ui`.
 *
 * Dark mode is *selected*, not auto-flipped: the resolver honors an explicit
 * `.dark`/`.light` class (or `data-theme`) on `<html>` — the Tailwind v4
 * convention wired up in `tokens.css` — and falls back to the OS
 * `prefers-color-scheme` only when nothing is set. Every chart component accepts
 * an optional `theme` override; when omitted it observes the document so canvas
 * charts re-render on a live theme toggle.
 */
import { useEffect, useState } from "react";

export type Theme = "light" | "dark";

/** Read the current theme from the DOM / OS. SSR-safe (returns `'light'`). */
export function detectTheme(): Theme {
  if (typeof document !== "undefined") {
    const el = document.documentElement;
    if (el.classList.contains("dark")) return "dark";
    if (el.classList.contains("light")) return "light";
    const attr = el.getAttribute("data-theme");
    if (attr === "dark" || attr === "light") return attr;
  }
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
}

/**
 * Resolve the active theme. Pass `explicit` to pin it (controlled); omit it to
 * track the document class + OS preference reactively.
 */
export function useResolvedTheme(explicit?: Theme): Theme {
  const [theme, setTheme] = useState<Theme>(() => explicit ?? detectTheme());

  useEffect(() => {
    if (explicit) {
      setTheme(explicit);
      return;
    }
    const update = (): void => setTheme(detectTheme());
    update();

    const media =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
    media?.addEventListener("change", update);

    let observer: MutationObserver | null = null;
    if (typeof MutationObserver !== "undefined" && typeof document !== "undefined") {
      observer = new MutationObserver(update);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class", "data-theme"],
      });
    }

    return () => {
      media?.removeEventListener("change", update);
      observer?.disconnect();
    };
  }, [explicit]);

  return explicit ?? theme;
}
